import { useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useRecentSearchQuery,
  useCreateRecentSearchMutation
} from "./useRecentSearchAPI";
import {
  RecentSearchItem,
  CreateRecentSearchRequest,
  GlobalSearchResult
} from "../types";

export const useRecentSearch = () => {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data: recentSearchData, isLoading, refetch } = useRecentSearchQuery();

  const createRecentSearchMutation = useCreateRecentSearchMutation();

  const recentSearches: RecentSearchItem[] =
    recentSearchData?.recentSearches || [];

  const createRecentSearch = useCallback(
    async (searchData: CreateRecentSearchRequest) => {
      try {
        setError(null);
        await createRecentSearchMutation.mutateAsync(searchData);
        queryClient.invalidateQueries({ queryKey: ["search/global/recent"] });
      } catch (err) {
        console.error("Failed to create recent search:", err);
        setError("Failed to save recent search");
      }
    },
    [createRecentSearchMutation, queryClient]
  );

  const createRecentSearchFromResult = useCallback(
    (query: string, result: GlobalSearchResult) => {
      const searchData: CreateRecentSearchRequest = {
        query,
        entityType: result.entityType,
        resultId: result.id,
        resultEntityType: result.entityType,
        resultName: result.name
      };
      return createRecentSearch(searchData);
    },
    [createRecentSearch]
  );

  const refreshRecentSearches = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    recentSearches,
    isLoading,
    error,
    createRecentSearch,
    createRecentSearchFromResult,
    refreshRecentSearches,
    isCreating: createRecentSearchMutation.isPending
  };
};
